/**
 * Arrastar o botão flutuante para outro canto da tela, e lembrar onde ficou.
 *
 * O deslocamento é relativo à posição original de `settings.position`, e não
 * uma coordenada absoluta: assim o botão continua respondendo à posição
 * configurada quando ninguém nunca o arrastou.
 */

import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

export interface LauncherOffset {
  x: number;
  y: number;
}

interface ViewportRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

const STORAGE_PREFIX = 'clixy:launcher-offset:';
const DRAG_THRESHOLD_PX = 6;
const VIEWPORT_MARGIN_PX = 8;

/** Hover de verdade, e não um dedo que só passa por cima da tela. */
export function supportsHover(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches
  );
}

function readStoredOffset(storageKey: string): LauncherOffset | null {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<LauncherOffset>;
    if (typeof parsed.x !== 'number' || typeof parsed.y !== 'number') return null;
    return { x: parsed.x, y: parsed.y };
  } catch {
    // Modo privado e quota cheia bloqueiam localStorage: perder a posição
    // lembrada é aceitável, o widget não deixa de funcionar por isso.
    return null;
  }
}

function writeStoredOffset(storageKey: string, offset: LauncherOffset): void {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(offset));
  } catch {
    // Mesma razão do read: é conveniência, não requisito.
  }
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Limita o deslocamento para o botão nunca ficar parcialmente fora da tela,
 * inclusive depois de girar o celular ou redimensionar a janela.
 *
 * `baseRect` é onde o botão estaria com deslocamento zero.
 */
export function clampOffsetToViewport(
  offset: LauncherOffset,
  baseRect: ViewportRect,
  viewport: { width: number; height: number },
  margin = VIEWPORT_MARGIN_PX,
): LauncherOffset {
  const minX = margin - baseRect.left;
  const maxX = viewport.width - margin - baseRect.width - baseRect.left;
  const minY = margin - baseRect.top;
  const maxY = viewport.height - margin - baseRect.height - baseRect.top;

  return {
    x: clamp(offset.x, Math.min(minX, maxX), Math.max(minX, maxX)),
    y: clamp(offset.y, Math.min(minY, maxY), Math.max(minY, maxY)),
  };
}

interface DragState {
  baseRect: ViewportRect;
  startClientX: number;
  startClientY: number;
  baseOffset: LauncherOffset;
  lastOffset: LauncherOffset;
  moved: boolean;
}

interface UseDraggableLauncherResult {
  ref: (element: HTMLButtonElement | null) => void;
  offset: LauncherOffset | null;
  dragging: boolean;
  onPointerDown: (event: PointerEvent) => void;
  /** true quando o clique que segue veio de um arrasto, não de uma intenção de abrir. */
  consumeDragClick: () => boolean;
}

export function useDraggableLauncher(publicKey: string): UseDraggableLauncherResult {
  const storageKey = `${STORAGE_PREFIX}${publicKey}`;

  const elementRef = useRef<HTMLButtonElement | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const justDraggedRef = useRef(false);

  const [offset, setOffset] = useState<LauncherOffset | null>(() => readStoredOffset(storageKey));
  const [dragging, setDragging] = useState(false);

  const offsetRef = useRef(offset);
  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  // Uma posição arrastada num monitor largo nasce fora da tela na volta de um
  // celular: reencaixa sempre que o viewport muda, não só ao carregar.
  useEffect(() => {
    function reclamp() {
      const current = offsetRef.current;
      const element = elementRef.current;
      if (!current || !element) return;

      const rect = element.getBoundingClientRect();
      const baseRect: ViewportRect = {
        left: rect.left - current.x,
        top: rect.top - current.y,
        width: rect.width,
        height: rect.height,
      };
      const next = clampOffsetToViewport(current, baseRect, {
        width: window.innerWidth,
        height: window.innerHeight,
      });

      if (next.x === current.x && next.y === current.y) return;
      setOffset(next);
      writeStoredOffset(storageKey, next);
    }

    window.addEventListener('resize', reclamp);
    window.addEventListener('orientationchange', reclamp);
    return () => {
      window.removeEventListener('resize', reclamp);
      window.removeEventListener('orientationchange', reclamp);
    };
  }, [storageKey]);

  const handlePointerDown = useCallback(
    (event: PointerEvent) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      const element = elementRef.current;
      if (!element) return;

      const baseOffset = offsetRef.current ?? { x: 0, y: 0 };
      const rect = element.getBoundingClientRect();

      const drag: DragState = {
        baseRect: {
          left: rect.left - baseOffset.x,
          top: rect.top - baseOffset.y,
          width: rect.width,
          height: rect.height,
        },
        startClientX: event.clientX,
        startClientY: event.clientY,
        baseOffset,
        lastOffset: baseOffset,
        moved: false,
      };
      dragRef.current = drag;

      function handleMove(moveEvent: PointerEvent) {
        const dx = moveEvent.clientX - drag.startClientX;
        const dy = moveEvent.clientY - drag.startClientY;

        if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
        drag.moved = true;
        setDragging(true);

        const candidate = { x: drag.baseOffset.x + dx, y: drag.baseOffset.y + dy };
        drag.lastOffset = clampOffsetToViewport(candidate, drag.baseRect, {
          width: window.innerWidth,
          height: window.innerHeight,
        });
        setOffset(drag.lastOffset);
      }

      function handleUp() {
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
        window.removeEventListener('pointercancel', handleUp);

        dragRef.current = null;
        setDragging(false);

        if (drag.moved) {
          justDraggedRef.current = true;
          writeStoredOffset(storageKey, drag.lastOffset);
        }
      }

      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
      window.addEventListener('pointercancel', handleUp);
    },
    [storageKey],
  );

  const consumeDragClick = useCallback(() => {
    if (!justDraggedRef.current) return false;
    justDraggedRef.current = false;
    return true;
  }, []);

  const setRef = useCallback((element: HTMLButtonElement | null) => {
    elementRef.current = element;
  }, []);

  return { ref: setRef, offset, dragging, onPointerDown: handlePointerDown, consumeDragClick };
}
